/**
 * Axios HTTP Client
 * Base API client with interceptors for authentication and logging
 * Tokens are stored in httpOnly cookies — browser sends them automatically
 */

import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { brandColors } from '@aegislab/design-tokens';
import { SCENARIO_STOP_RULES_V2_MEDIA_TYPE, type AuthResponse } from '@aegislab/shared';
import { config } from '../config';
import { useAuthStore } from '../model/authStore';
import { logger } from '../lib/logger';
import {
  captureApiError,
  refreshFailureCode,
  refreshFailureErrorType,
  shouldCaptureApiError,
} from './sentryCapture';
import { withCrossTabRefreshLock } from './refreshLock';
import { scheduleNetworkReport } from './networkReporting';
import { bindNetworkReportScope } from './networkReportScope';
import { trackNetworkClient } from './trackNetworkClient';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean;
    _sentryVisibility?: DocumentVisibilityState;
  }
}

const apiLogger = {
  request: (config: InternalAxiosRequestConfig) => {
    logger.log(
      `%c[API Request] ${config.method?.toUpperCase()} ${config.url}`,
      `color: ${brandColors.primary.default}; font-weight: bold`,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      { headers: config.headers, data: config.data, params: config.params }
    );
  },
  response: (response: AxiosResponse) => {
    logger.log(
      `%c[API Response] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
      'color: #22c55e; font-weight: bold',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      { data: response.data, headers: response.headers }
    );
  },
  error: (error: AxiosError) => {
    logger.error(
      `%c[API Error] ${error.response?.status ?? 'NETWORK'} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
      'color: #ef4444; font-weight: bold',
      { message: error.message, data: error.response?.data, status: error.response?.status }
    );
  },
};

/**
 * Axios instance configured for API requests
 * withCredentials: true ensures httpOnly cookies are sent automatically
 */
export const apiClient = trackNetworkClient(
  axios.create({
    baseURL: config.apiUrl,
    timeout: 10000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  })
);

/**
 * Request interceptor - Logs requests
 */
apiClient.interceptors.request.use(
  (requestConfig: InternalAxiosRequestConfig) => {
    // 목록·상세·초안·쓰기 응답에서 두 중단 기준을 빠짐없이 받는 계약을 명시한다.
    if (/^\/scenarios(?:[/?]|$)/.test(requestConfig.url ?? '')) {
      requestConfig.headers.set('Accept', SCENARIO_STOP_RULES_V2_MEDIA_TYPE);
    }
    bindNetworkReportScope(requestConfig);
    requestConfig._sentryVisibility =
      typeof document === 'undefined' ? undefined : document.visibilityState;
    apiLogger.request(requestConfig);
    return requestConfig;
  },
  (error: AxiosError) => {
    apiLogger.error(error);
    return Promise.reject(error);
  },
  { synchronous: true }
);

/**
 * Response interceptor - Handles 401 with cookie-based refresh
 */
let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: unknown) => void; reject: (e: unknown) => void }> = [];

function processQueue(error: unknown) {
  failedQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(undefined)));
  failedQueue = [];
}

function replayAfterRefresh(request: InternalAxiosRequestConfig) {
  const replay: InternalAxiosRequestConfig = { ...request, _sentryAuthReplay: true };
  return apiClient(replay);
}

apiClient.interceptors.response.use(
  (response) => {
    apiLogger.response(response);
    return response;
  },
  async (error: AxiosError) => {
    if (!error.response) {
      scheduleNetworkReport(error);
    } else if (shouldCaptureApiError(error)) {
      captureApiError(error);
    }
    apiLogger.error(error);
    const originalRequest = error.config;

    const isAuthEndpoint =
      originalRequest?.url?.startsWith('/auth/login') ||
      originalRequest?.url?.startsWith('/auth/register') ||
      originalRequest?.url?.startsWith('/auth/logout');

    const hasAuthContext = useAuthStore.getState().user != null;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !isAuthEndpoint &&
      !originalRequest._retry &&
      hasAuthContext
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: () => resolve(replayAfterRefresh(originalRequest)),
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // AXIOM-WEB-2 Tier 1: cross-tab refresh sync. 다중 탭/PWA window 가 같은
        // cookie 로 동시에 refresh 를 fire 해서 BE token-reuse-detection 이
        // revokeAllUserTokens 를 트리거하는 race 차단. 다른 탭이 방금 refresh 완료
        // 했으면 (grace window 안) 우리는 자기 refresh skip 하고 새 cookie 로 retry 만.
        const { result: refreshResponse, usedGrace } = await withCrossTabRefreshLock(() =>
          axios.post<AuthResponse>(
            `${config.apiUrl}/auth/refresh`,
            {},
            {
              withCredentials: true,
              timeout: 10_000,
              transitional: { clarifyTimeoutError: true },
            }
          )
        );
        if (!usedGrace && refreshResponse?.data.user) {
          useAuthStore.getState().setAuth(refreshResponse.data.user);
        }
        processQueue(null);
        return replayAfterRefresh(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // 갱신 서버의 401만 세션 무효로 확정한다. 응답 없음·시간 초과·중단·5xx는
        // 세션을 유지하고 오류를 호출자에 전파해 기존 재시도·오류 안내로 처리한다.
        if (axios.isAxiosError(refreshError) && refreshError.response?.status === 401) {
          useAuthStore.getState().logout();
        }
        const refreshErrorType = refreshFailureErrorType(refreshError);
        captureApiError(error, {
          auth_recovery: 'failed',
          refresh_error_code: refreshFailureCode(refreshError),
          ...(refreshErrorType ? { error_type: refreshErrorType } : {}),
        });
        return Promise.reject(
          refreshError instanceof Error ? refreshError : new Error(String(refreshError))
        );
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
